<script setup>
import { ref, computed, watch, nextTick } from 'vue';
import { vOnClickOutside } from '@vueuse/components';
import { useElementBounding } from '@vueuse/core';
import { useI18n } from 'vue-i18n';
import Icon from 'dashboard/components-next/icon/Icon.vue';
import { useDropdownPosition } from 'dashboard/composables/useDropdownPosition';
import TeleportWithDirection from 'dashboard/components-next/TeleportWithDirection.vue';

const props = defineProps({
  options: {
    type: Array,
    default: () => [],
    validator: options =>
      options.every(
        opt => typeof opt === 'object' && 'value' in opt && 'label' in opt
      ),
  },
  groups: {
    type: Array,
    default: () => [],
    validator: groups =>
      groups.every(
        group =>
          'label' in group &&
          Array.isArray(group.options) &&
          group.options.every(opt => 'value' in opt && 'label' in opt)
      ),
  },
  placeholder: {
    type: String,
    default: '',
  },
  disabled: {
    type: Boolean,
    default: false,
  },
  error: {
    type: String,
    default: '',
  },
  message: {
    type: String,
    default: '',
  },
  messageClass: {
    type: String,
    default: '',
  },
  searchable: {
    type: Boolean,
    default: false,
  },
  searchPlaceholder: {
    type: String,
    default: '',
  },
  emptyState: {
    type: String,
    default: '',
  },
  ariaLabel: {
    type: String,
    default: '',
  },
  teleport: {
    type: Boolean,
    default: true,
  },
});

const modelValue = defineModel({
  type: [String, Number, Boolean, null],
  default: null,
});

const isOpen = ref(false);
const searchQuery = ref('');
const triggerRef = ref(null);
const listboxRef = ref(null);
const searchInputRef = ref(null);
const { t } = useI18n();

// Normaliza o valor selecionado: se for string numérica, converte para Number
// para evitar mismatch de tipo ao comparar com IDs de API (que vêm como Number).
// Preserva substituto em qualquer outro caso (string não-numérica, Boolean, etc.).
const normalizeValue = value => {
  if (
    typeof value === 'string' &&
    value !== '' &&
    !Number.isNaN(Number(value))
  ) {
    return Number(value);
  }
  return value;
};

const flatOptions = computed(() =>
  props.groups.length
    ? props.groups.flatMap(group => group.options)
    : props.options
);

const filterOptionBySearch = option =>
  (option.label || '').toLowerCase().includes(searchQuery.value.toLowerCase());

const filteredOptions = computed(() =>
  searchQuery.value ? props.options.filter(filterOptionBySearch) : props.options
);

const filteredGroups = computed(() => {
  if (!searchQuery.value) return props.groups;

  return props.groups
    .map(group => ({
      ...group,
      options: group.options.filter(filterOptionBySearch),
    }))
    .filter(group => group.options.length);
});

const visibleFlatOptions = computed(() =>
  props.groups.length
    ? filteredGroups.value.flatMap(group => group.options)
    : filteredOptions.value
);

const enabledOptions = computed(() =>
  visibleFlatOptions.value.filter(opt => !opt.disabled)
);

const selectedOptionLabel = computed(() => {
  const selected = flatOptions.value.find(
    opt => opt.value === modelValue.value
  );
  return selected ? selected.label : '';
});

const { fixedPosition, position, updatePosition } = useDropdownPosition(
  triggerRef,
  listboxRef,
  isOpen
);

// Reproduz a largura do trigger no listbox teleportado (que está em <body>).
const triggerBounds = useElementBounding(triggerRef);
const dropdownPosition = computed(() =>
  props.teleport ? fixedPosition.value : position.value
);
const listboxMinWidth = computed(() => {
  if (!props.teleport) return '100%';
  return triggerBounds.width.value
    ? `${triggerBounds.width.value}px`
    : undefined;
});
const listboxStyle = computed(() => ({
  ...dropdownPosition.value.style,
  minWidth: listboxMinWidth.value,
}));

const handleSelect = value => {
  modelValue.value = normalizeValue(value);
  isOpen.value = false;
  searchQuery.value = '';
  triggerRef.value?.focus();
};

const closeDropdown = () => {
  isOpen.value = false;
  searchQuery.value = '';
};

const openDropdown = async () => {
  if (props.disabled) return;
  isOpen.value = true;
  await nextTick();
  updatePosition();
  if (props.searchable) searchInputRef.value?.focus();
};

const toggleDropdown = async () => {
  if (props.disabled) return;
  if (isOpen.value) {
    closeDropdown();
  } else {
    await openDropdown();
  }
};

const focusOptionByIndex = index => {
  const opts = enabledOptions.value;
  if (!opts.length) return;
  const wrapped = (index + opts.length) % opts.length;
  const target = opts[wrapped];
  nextTick(() => {
    const el = listboxRef.value?.querySelector(
      `[data-option-value="${CSS.escape(String(target.value))}"]`
    );
    el?.focus();
  });
};

const handleTriggerKeydown = async event => {
  if (props.disabled) return;
  switch (event.key) {
    case 'Enter':
    case ' ':
    case 'ArrowDown':
      event.preventDefault();
      if (!isOpen.value) await openDropdown();
      focusOptionByIndex(0);
      break;
    case 'ArrowUp':
      event.preventDefault();
      if (!isOpen.value) await openDropdown();
      focusOptionByIndex(enabledOptions.value.length - 1);
      break;
    case 'Escape':
      if (isOpen.value) {
        event.preventDefault();
        closeDropdown();
      }
      break;
    default:
      break;
  }
};

const handleOptionKeydown = (event, option) => {
  const opts = enabledOptions.value;
  const currentIndex = opts.findIndex(o => o.value === option.value);
  switch (event.key) {
    case 'ArrowDown':
      event.preventDefault();
      focusOptionByIndex(currentIndex + 1);
      break;
    case 'ArrowUp':
      event.preventDefault();
      focusOptionByIndex(currentIndex - 1);
      break;
    case 'Home':
      event.preventDefault();
      focusOptionByIndex(0);
      break;
    case 'End':
      event.preventDefault();
      focusOptionByIndex(opts.length - 1);
      break;
    case 'Escape':
      event.preventDefault();
      closeDropdown();
      triggerRef.value?.focus();
      break;
    case 'Tab':
      closeDropdown();
      break;
    default:
      break;
  }
};

// Fecha o dropdown se o trigger sair da viewport ou houver scroll.
// useDropdownPosition já atualiza a posição reativamente; este watch garante que
// o estilo é recalculado quando o listbox monta após Teleport.
watch(isOpen, async open => {
  if (open) {
    await nextTick();
    updatePosition();
  }
});

const handleClickOutside = event => {
  if (triggerRef.value?.contains(event.target)) return;
  closeDropdown();
};

const searchPlaceholderLabel = computed(
  () => props.searchPlaceholder || t('DROPDOWN_MENU.SEARCH_PLACEHOLDER')
);

const emptyStateLabel = computed(
  () => props.emptyState || t('DROPDOWN_MENU.EMPTY_STATE')
);

const hasVisibleOptions = computed(() => visibleFlatOptions.value.length > 0);
</script>

<template>
  <div class="relative w-full min-w-0 select-none">
    <!-- Botão de Gatilho (Trigger) -->
    <button
      ref="triggerRef"
      type="button"
      role="combobox"
      :aria-expanded="isOpen"
      :aria-label="ariaLabel || undefined"
      aria-haspopup="listbox"
      :aria-disabled="disabled"
      :disabled="disabled"
      class="flex items-center justify-between w-full min-w-0 h-10 px-3 py-2 text-sm transition-all duration-500 ease-in-out border-0 border-none rounded-lg outline outline-1 outline-offset-[-1px] cursor-pointer select-none bg-n-alpha-black2 text-n-slate-12"
      :class="{
        'outline-n-weak dark:outline-n-weak hover:outline-n-slate-6 dark:hover:outline-n-slate-6 focus:outline-n-brand dark:focus:outline-n-brand':
          !error && !disabled,
        'outline-n-red-9 dark:outline-n-red-9 focus:outline-n-red-9 dark:focus:outline-n-red-9':
          error && !disabled,
        'outline-n-weak cursor-not-allowed opacity-60': disabled,
        'outline-n-brand': isOpen && !disabled,
      }"
      @click="toggleDropdown"
      @keydown="handleTriggerKeydown"
    >
      <span class="flex-1 min-w-0 text-left truncate text-n-slate-12">
        <template v-if="selectedOptionLabel">{{
          selectedOptionLabel
        }}</template>
        <template v-else-if="placeholder">{{ placeholder }}</template>
        <template v-else>&#160;</template>
      </span>
      <Icon
        icon="i-lucide-chevron-down"
        class="size-4 text-n-slate-11 shrink-0 transition-transform duration-200 ml-2"
        :class="{ 'rotate-180': isOpen, 'opacity-50': disabled }"
      />
    </button>

    <!-- Lista de opções: teleportada ao body para escapar de containers com overflow:hidden -->
    <component :is="teleport ? TeleportWithDirection : 'div'" to="body">
      <div
        v-if="isOpen && !disabled"
        ref="listboxRef"
        v-on-click-outside="handleClickOutside"
        role="listbox"
        data-popover-content
        :class="[dropdownPosition.class, { 'absolute z-50': !teleport }]"
        :style="listboxStyle"
        class="bg-n-solid-1 border border-n-weak rounded-lg shadow-lg max-h-60 overflow-hidden min-w-[12rem]"
      >
        <div
          v-if="searchable"
          class="relative flex items-center h-10 border-0 border-b border-solid border-n-weak"
        >
          <Icon
            icon="i-lucide-search"
            class="absolute size-4 text-n-slate-11 ltr:left-3 rtl:right-3"
          />
          <input
            ref="searchInputRef"
            v-model="searchQuery"
            type="search"
            class="reset-base w-full h-full bg-transparent text-sm text-n-slate-12 placeholder:text-n-slate-10 outline-none ltr:pl-10 ltr:pr-3 rtl:pr-10 rtl:pl-3"
            :placeholder="searchPlaceholderLabel"
            @keydown.escape.stop.prevent="closeDropdown"
            @keydown.arrow-down.prevent="focusOptionByIndex(0)"
            @keydown.arrow-up.prevent="
              focusOptionByIndex(enabledOptions.length - 1)
            "
          />
        </div>
        <div class="max-h-52 overflow-y-auto py-1">
          <template v-if="groups.length">
            <div
              v-for="group in filteredGroups"
              :key="group.label"
              role="group"
            >
              <div
                class="px-3 py-1 text-xs font-semibold text-n-slate-9 bg-n-alpha-2 select-none"
              >
                {{ group.label }}
              </div>
              <button
                v-for="option in group.options"
                :key="option.value"
                type="button"
                role="option"
                :aria-selected="option.value === modelValue"
                :data-option-value="String(option.value)"
                class="group flex items-center justify-between w-full px-3 py-2 text-left text-sm transition-colors text-n-slate-12 cursor-pointer select-none focus:outline-none"
                :class="{
                  'bg-n-alpha-2 font-medium': option.value === modelValue,
                  'hover:bg-n-brand hover:text-white focus:bg-n-brand focus:text-white':
                    !option.disabled,
                  'opacity-50 cursor-not-allowed': option.disabled,
                }"
                :disabled="option.disabled"
                @click="handleSelect(option.value)"
                @keydown.enter.prevent="handleSelect(option.value)"
                @keydown.space.prevent="handleSelect(option.value)"
                @keydown="handleOptionKeydown($event, option)"
              >
                <span class="truncate">{{ option.label }}</span>
                <Icon
                  v-if="option.value === modelValue"
                  icon="i-lucide-check"
                  class="size-4 shrink-0 text-n-brand group-hover:text-white group-focus:text-white"
                />
              </button>
            </div>
          </template>
          <template v-else>
            <button
              v-for="option in filteredOptions"
              :key="option.value"
              type="button"
              role="option"
              :aria-selected="option.value === modelValue"
              :data-option-value="String(option.value)"
              class="group flex items-center justify-between w-full px-3 py-2 text-left text-sm transition-colors text-n-slate-12 cursor-pointer select-none focus:outline-none"
              :class="{
                'bg-n-alpha-2 font-medium': option.value === modelValue,
                'hover:bg-n-brand hover:text-white focus:bg-n-brand focus:text-white':
                  !option.disabled,
                'opacity-50 cursor-not-allowed': option.disabled,
              }"
              :disabled="option.disabled"
              @click="handleSelect(option.value)"
              @keydown.enter.prevent="handleSelect(option.value)"
              @keydown.space.prevent="handleSelect(option.value)"
              @keydown="handleOptionKeydown($event, option)"
            >
              <span class="truncate">{{ option.label }}</span>
              <Icon
                v-if="option.value === modelValue"
                icon="i-lucide-check"
                class="size-4 shrink-0 text-n-brand group-hover:text-white group-focus:text-white"
              />
            </button>
          </template>
          <div
            v-if="!hasVisibleOptions"
            class="px-3 py-2 text-sm text-n-slate-11"
          >
            {{ emptyStateLabel }}
          </div>
        </div>
      </div>
    </component>
    <p
      v-if="error || message"
      class="mt-1.5 text-label-small leading-4"
      :class="[error ? 'text-n-ruby-9' : 'text-n-slate-11', messageClass]"
    >
      {{ error || message }}
    </p>
  </div>
</template>
